/**
 * English translations for Okulus Photography Platform
 */
export const en = {
  common: {
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    search: "Search",
    filter: "Filter",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    confirm: "Confirm",
    close: "Close",
    back: "Back",
    next: "Next",
    previous: "Previous",
    upload: "Upload",
    download: "Download",
    downloaded: "Downloaded",
    view: "View",
    video: "Video",
    print: "Print",
    digital: "Digital",
    image: "Image",
    actions: "Actions",
    status: "Status",
    date: "Date",
    time: "Time",
    total: "Total",
    price: "Price",
    quantity: "Quantity",
    yes: "Yes",
    no: "No",
    page: "Page",
    of: "of",
    showing: "Showing",
    refresh: "Refresh",
    saving: "Saving...",
    optional: "Optional",
    backToGallery: "Back to Gallery",
  },

  auth: {
    login: "Login",
    logout: "Logout",
    register: "Register",
    email: "Email",
    password: "Password",
    forgotPassword: "Forgot password?",
    resetPassword: "Reset password",
    welcomeBack: "Welcome back",
    signIn: "Sign in",
    signOut: "Sign out",
      pleaseLoginToContinue: "Please sign in to continue",
    notRegistered: "Not registered?",
    alreadyHaveAccount: "Already have an account?",
    loginSuccess: "Login successful!",
    registerSuccess: "Account created successfully!",
    passwordResetSuccess: "Password changed successfully!",
    passwordsDontMatch: "Passwords do not match",
    passwordTooShortAuth: "Password must be at least 8 characters",
    enterEmail: "Please enter an email address",
    accountNotActivated: "Account is not activated. Please contact the administrator.",
  },

  nav: {
    dashboard: "Dashboard",
    projects: "Projects",
    images: "Images",
    accessCodes: "Access Codes",
    orders: "Orders",
    settings: "Settings",
    gallery: "Gallery",
    cart: "Cart",
    profile: "Profile",
    analytics: "Analytics",
    adminUsers: "Users",
  },

  settings: {
    title: "Settings",
    general: "General",
    security: "Security",
    system: "System",
    systemDescription: "System name and base URL (global)",
    systemName: "System Name",
    baseUrl: "Base URL",
    systemNameGlobalNote: "This value is managed centrally and shared by all users.",
    baseUrlHelp: "This base URL is used for QR codes and links and is identical for every user.",
    codeLoginLimit: "Max logins per access code",
    codeLoginLimitHelp: "How many times an access code from PDF can be used",
    maxManualDigitalImages: "Max. digital images (manual download)",
    maxManualDigitalImagesHelp: "Maximum number of digital images per login code without a package",
    maxManualDigitalImagesReached: "Maximum number of digital images reached",
    save: "Save",
    pricing: "Pricing",
    shippingRates: "Shipping Rates",
    shippingRatesTitle: "Shipping rates by country",
    shippingRatesDescription: "Configure shipping prices per destination. Only active countries appear during checkout.",
    shippingRatesCountry: "Country",
    shippingRatesCost: "Cost",
    shippingRatesActive: "Active",
    shippingRatesActions: "Actions",
    shippingRatesAddButton: "Add country",
    shippingRatesAddTitle: "Add new country",
    shippingRatesSelectPlaceholder: "Select country...",
    shippingRatesCustomPlaceholder: "Enter country name",
    shippingRatesCustomOption: "Other country...",
    shippingRatesCostInputLabel: "Shipping cost (€)",
    shippingRatesCostInputPlaceholder: "0.00",
    shippingRatesEmpty: "No shipping rates configured yet",
    shippingRatesToastSaved: "Shipping rate saved",
    shippingRatesToastDeleted: "Shipping rate deleted",
    shippingRatesToastErrorPrefix: "Error:",
    shippingRatesFormMissing: "Please enter a country and cost",
    shippingRatesDeleteConfirm: "Delete this shipping rate?",
    shippingRatesCountryOptions: [
      "Germany",
      "Austria",
      "Switzerland",
      "Bosnia and Herzegovina",
      "Croatia",
      "Serbia",
      "Montenegro",
      "Slovenia",
      "North Macedonia",
    ],
    watermark: "Watermark",
    paypal: "PayPal",
    storage: "Storage",
    gallery: "Gallery",
    qrCode: "QR Code",
    notifications: "Notifications",
    localization: "Localization",
    legal: "Legal",
    legalTab: {
      title: "Legal pages",
      description: "Imprint, privacy, shipping and other footer pages.",
      createButton: "New page",
      defaultsButton: "Insert default pages",
      emptyState: "No pages yet.",
      inactiveBadge: "inactive",
      validationError: "Please fill out all fields",
      deleteConfirm: "Delete this page?",
      toastCreated: "Page created",
      toastUpdated: "Page updated",
      toastDeleted: "Page deleted",
      defaultsAlready: "Default pages already exist",
      defaultsCreated: "Default pages added",
      defaultsFailed: "Failed to add default pages",
      defaultContentNote: "You can customize the content under Settings › Legal.",
      dialog: {
        createTitle: "New page",
        editTitle: "Edit page",
        description: "Slug is used in the URL. Content can include HTML.",
        slugLabel: "Slug",
        titleLabel: "Title",
        sortOrderLabel: "Sort order",
        activeLabel: "Active",
        languageLabel: "Language",
        contentLabel: "Content ({lang})",
        slugPlaceholder: "imprint",
        titlePlaceholder: "Imprint",
      },
      buttons: {
        cancel: "Cancel",
        save: "Save",
        saving: "Saving...",
      },
      defaultPages: {
        impressum: "Imprint",
        privacy: "Privacy",
        terms: "Terms of Service",
        contact: "Contact",
        shipping: "Shipping",
        payment: "Payment methods",
      },
    },

    pricePerImage: "Price per Image",
    pricePerVideo: "Price per Video",
    currency: "Currency",

    watermarkEnabled: "Enable watermark",
    watermarkText: "Watermark text",
    watermarkLogo: "Watermark logo",
    watermarkPosition: "Watermark position",
    watermarkOpacity: "Watermark opacity",
    center: "Center",
    topLeft: "Top left",
    topRight: "Top right",
    bottomLeft: "Bottom left",
    bottomRight: "Bottom right",
    tiled: "Tiled",

    paypalMode: "PayPal mode",
    sandbox: "Sandbox (test)",
    live: "Live (production)",
    paypalClientId: "PayPal Client ID",
    paypalClientSecret: "PayPal Client Secret",

    gcs: "Cloudflare R2",
    gcsProjectId: "R2 Access Key ID",
    gcsBucketName: "R2 Bucket Name",
    gcsCredentials: "R2 Secret Access Key",

    settingsSaved: "Settings saved",
    settingsFailed: "Failed to save settings",

    language: "Language",
    timezone: "Timezone",
    dateFormat: "Date format",

    pleaseLogin: "Please log in",
    pricingDescription: "Configure prices for images",
    watermarkDescription: "Configure watermark for images",
    gcsDescription: "Configure Cloudflare R2 storage for images",
    r2Title: "Cloudflare R2",
    r2Description: "Configure Cloudflare R2 storage for images",
    r2AccessKeyId: "R2 Access Key ID",
    r2SecretAccessKey: "R2 Secret Access Key",
    r2Endpoint: "R2 Endpoint (accountid.r2.cloudflarestorage.com)",
    r2BucketName: "R2 Bucket Name",
    r2TestConnection: "Test R2 Connection",
    r2TestSuccess: "R2 connection successful",
    r2TestFailed: "R2 connection failed",
    r2FetchUsage: "Fetch Usage",
    r2UsageLabel: "Bucket Usage",
    r2UsageObjects: "Objects",
    r2UsageBytes: "Total Size",
    r2UsageFailed: "Failed to fetch usage",
    emailNotificationsEnabled: "Enable email notifications",
    notificationEmail: "Notification email",
    smtpPassword: "SMTP password",
    paypalDescription: "Configure PayPal for payments",
    emailNotifications: "Email notifications",
    emailNotificationsDescription: "Configure SMTP server for sending email notifications",
    enableEmailNotifications: "Enable email notifications",
    manageDescription: "Manage your platform settings",
    localizationDescription: "Choose interface language",
    downloadLinkDays: "Download link validity (days)",
    downloadLinkDaysHelp: "How long customers can use the ZIP download",
    smtpSettings: "SMTP Settings",
    smtpHost: "SMTP Host",
    smtpPort: "SMTP Port",
    smtpUser: "SMTP User",
    smtpFrom: "Sender email",
    smtpTestConnection: "Test SMTP",
    smtpTestSuccess: "SMTP test email sent",
    smtpTestFailed: "SMTP test failed",
    smtpTestRecipient: "Test recipient",
    sendInvoiceEmail: "Automatically send invoice via email",
    companyInfo: "Company / Invoicing",
    companyInfoDescription: "Store company data used for invoices and legal documents",
    companyName: "Company name",
    companyAddressLine1: "Address line 1",
    companyAddressLine2: "Address line 2",
    companyPostalCode: "Postal code",
    companyCity: "City",
    companyCountry: "Country",
    companyVatId: "VAT ID",
    companyTaxNumber: "Tax number",
    companyTaxRate: "Tax rate (%)",
    companyEmail: "Company email",
    companyPhone: "Company phone",
    companyWebsite: "Website",
    companyBankName: "Bank name",
    companyIban: "IBAN",
    companySwift: "SWIFT/BIC",
    companyLogoGcsKey: "Logo storage key",
    companyLogoUploadLabel: "Company logo upload",
    companyLogoUploadButton: "Upload Logo",
    companyLogoPreview: "Current logo",
    companyLogoNoFile: "No logo selected",
    companyInvoiceFooter: "Invoice footer notes",
    placeholders: {
      pricePerImage: "5.00",
      pricePerVideo: "10.00",
      watermarkText: "Photography",
      companyName: "My Company Ltd",
      companyVatId: "DE123456789",
      companyTaxNumber: "123/456/78901",
      companyTaxRate: "19.00",
      companyAddressLine1: "Street 1",
      companyAddressLine2: "Floor / Addition",
      companyPostalCode: "12345",
      companyCity: "Berlin",
      companyCountry: "Germany",
      companyEmail: "info@company.com",
      companyPhone: "+49 30 123456",
      companyWebsite: "https://www.company.com",
      companyBankName: "Bank name",
      companyIban: "DE89 3704 0044 0532 0130 00",
      companySwift: "COBADEFFXXX",
      companyLogoGcsKey: "logo/path.png",
      companyInvoiceFooter: "Additional invoice notes",
      paypalClientId: "PayPal Client ID",
      paypalClientSecret: "PayPal Client Secret",
      r2AccessKeyId: "ACCESS_KEY_ID",
      r2SecretAccessKey: "SECRET_ACCESS_KEY",
      r2Endpoint: "accountid.r2.cloudflarestorage.com",
      r2BucketName: "bucket-name",
      smtpHost: "smtp.gmail.com",
      smtpPort: "587",
      smtpUser: "your.email@gmail.com",
      smtpPassword: "password",
      smtpFrom: "noreply@example.com",
      notificationEmail: "your.name@example.com",
      smtpTestRecipient: "recipient@example.com",
    },
    storageMonitoring: "Storage Monitoring",
    storageMonitoringDescription: "Monitor your upload storage usage",
    storageProviderTitle: "Storage Provider",
    storageProviderDescription: "Configure where your originals and previews are stored.",
    storageProviderLabel: "Provider",
    storageProviderPlaceholder: "Choose storage provider",
    storageProviderHelp: "Cloud storage prevents data loss when new releases are deployed.",
    localStorageWarningTitle: "Local storage is only for testing",
    localStorageWarning: "Uploads stay on the app server and will be wiped on every deployment. Use R2 or GCS for production.",
    useLocalStorage: "Switch to local storage",
    storageUsed: "Used",
    storageRemaining: "Available",
    storageUsagePercent: "Usage",
    storageLimit: "Limit",
    storageOverLimit: "Storage limit exceeded!",
    storageOverLimitMessage: "Please increase the limit or delete files.",
    storageNearLimit: "Storage almost full",
    storageNearLimitMessage: "Consider increasing the limit.",
    configureStorageLimit: "Configure Storage Limit",
    storageLimitGB: "Limit (GB)",
    storageLimitHelp: "Set the maximum storage limit for uploads in gigabytes.",
    currentLimit: "Current Limit",
    storageLoadError: "Error loading storage information",
    storageLimitUpdated: "Storage limit updated",
    storageLimitPasswordTitle: "Super Password Required",
    storageLimitPasswordDescription: "Enter the super password to change the storage limit.",
    password: "Password",
    passwordCorrect: "Access granted",
    passwordIncorrect: "Wrong password",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    passwordMismatch: "Passwords do not match",
    passwordTooShort: "Password must be at least 6 characters long",
    currentPasswordRequired: "Current password is required",
    changeSuperPassword: "Change Super Password",
    changeSuperPasswordDesc: "Change the password that protects sensitive settings.",
    twoFactorAuth: "Two-Factor Authentication",
    twoFactorAuthDescription: "Enhance your account security with 2FA",
    twoFactorEnabled: "2FA is enabled",
    twoFactorDisabled: "2FA is disabled",
    twoFactorProtected: "Your account is protected by 2FA",
    twoFactorNotProtected: "Enable 2FA for additional security",
    twoFactorBackupCodes: "Backup Codes",
    twoFactorBackupCodesRemaining: "{count} unused codes available",
    twoFactorGenerateNew: "Generate new",
    twoFactorImportantNote: "Important Note",
    twoFactorAuthenticatorNote: "Use an authenticator app like Google Authenticator, Microsoft Authenticator, or Authy to generate your 2FA codes.",
    twoFactorEnableTitle: "Enable Two-Factor Authentication",
    twoFactorEnableDescription: "Scan the QR code with your authenticator app",
    twoFactorManualCode: "Or enter this code manually:",
    twoFactorVerificationCode: "Verification code from your app",
    twoFactorVerificationCodePlaceholder: "123456",
    twoFactorDisableTitle: "Disable Two-Factor Authentication",
    twoFactorDisableDescription: "Enter your password to disable 2FA",
    twoFactorDisableWarning: "⚠️ Warning: Disabling 2FA reduces your account security.",
    twoFactorBackupCodesTitle: "Your Backup Codes",
    twoFactorBackupCodesDescription: "Save these codes in a secure location. Each code can only be used once.",
    twoFactorBackupCodesCopyAll: "Copy all codes",
    twoFactorBackupCodesImportant: "⚠️ Important: These codes are only shown once. Save them securely!",
    twoFactorBackupCodesSaved: "I have saved the codes",
    twoFactorLoginTitle: "Two-Factor Authentication",
    twoFactorLoginDescription: "Enter the 6-digit code from your authenticator app",
    twoFactorLoginCode: "2FA Code",
    twoFactorLoginCodePlaceholder: "123456",
    twoFactorLoginBackupHint: "You can also use a backup code",
    twoFactorLoginVerify: "Verify",
    twoFactorLoginBack: "Back to Login",
    twoFactorEnterCode: "Please enter your 2FA code",
    twoFactorInvalidCode: "Invalid 2FA code",
    twoFactorActivated: "Two-Factor Authentication activated!",
    twoFactorDeactivated: "Two-Factor Authentication deactivated",
    twoFactorBackupCodesGenerated: "New backup codes generated",
    twoFactorCodeCopied: "Copied to clipboard",
    twoFactorAllCodesCopied: "All backup codes copied",
  },

  printFormats: {
    title: "Print Formats",
    newFormat: "New Format",
    editFormat: "Edit Format",
    noFormatsMessage: "No formats defined. Create your first format.",
    dialogDescription: "Define the properties of the print format",
    name: "Name",
    width: "Width",
    height: "Height",
    unit: "Unit",
    price: "Price ({currency})",
    active: "Active",
    cancel: "Cancel",
    save: "Save",
    deleteConfirm: "Really delete format?",
    created: "Format created",
    updated: "Format updated",
    deleted: "Format deleted",
    namePlaceholder: "e.g. 10x15 cm Standard",
    passwordCorrect: "Access granted",
    passwordIncorrect: "Incorrect password",
  },

  adminUsers: {
    title: "User Management",
    back: "Back",
    searchPlaceholder: "Search (Email/Name)",
    createTitle: "Create new user",
    email: "Email",
    name: "Name",
    password: "Password",
    role: "Role",
    roleUser: "User",
    roleAdmin: "Admin",
    active: "Active",
    create: "Create",
    listTitle: "User list",
    configure: "Configure",
    configureTitle: "Manage settings for {name}",
    configureDescription: "Assign storage provider, language defaults, and invoicing details for this user.",
    storageTab: "Storage",
    localizationTab: "Language",
    companyTab: "Company",
    noUserSelected: "No user selected",
    loadingSettings: "Loading settings...",
    failedToLoadSettings: "Could not load settings",
    storageProviderLabel: "Storage provider",
    storageOptionLocal: "Local uploads",
    storageOptionR2: "Cloudflare R2",
    storageOptionB2: "Backblaze B2",
    storageOptionGcs: "Google Cloud Storage",
    storageLocalDescription: "Files remain on the local server (only recommended for demos).",
    storageR2Description: "Provide the Cloudflare R2 credentials used for uploads and downloads.",
    storageB2Description: "Provide the Backblaze B2 credentials used for uploads and downloads.",
    storageGcsDescription: "Provide the Google Cloud bucket and service account JSON used for uploads.",
    timezoneLabel: "Timezone",
    timezoneHint: "Example: Europe/Sarajevo",
    fields: {
      r2AccessKeyId: "Access Key ID",
      r2SecretAccessKey: "Secret Access Key",
      r2Endpoint: "Endpoint",
      r2BucketName: "Bucket name",
      b2KeyId: "Key ID",
      b2ApplicationKey: "Application key",
      b2BucketName: "Bucket name",
      b2Endpoint: "Endpoint (optional)",
      b2Region: "Region",
      gcsProjectId: "Project ID",
      gcsBucketName: "Bucket name",
      gcsCredentialsJson: "Service account JSON",
    },
    actions: "Actions",
    delete: "Delete",
    noneFound: "No users found",
    activateUser: "Activate User",
    activateUserDescription: "Assign a storage quota before activating the user.",
    storageQuota: "Storage Quota",
    storageQuotaHelp: "Recommended: 50GB for regular users",
    activate: "Activate",
    toasts: {
      created: "User created",
      updated: "User updated",
      deleted: "User deleted",
      createFailed: "Creation failed",
      updateFailed: "Update failed",
      activateFailed: "Activation failed",
      roleFailed: "Role change failed",
      deleteFailed: "Delete failed",
      storageSaved: "Storage settings saved",
      storageFailed: "Failed to save storage settings",
      localizationSaved: "Localization updated",
      localizationFailed: "Failed to update localization",
      companySaved: "Company info saved",
      companyFailed: "Failed to save company info",
    },
  },

  legal: {
    contactTitle: "Kontakt",
    contactDefault: "Inhalt kann in Einstellungen › Legal angepasst werden.",
    impressumTitle: "Impressum",
    impressumDefault: "Test",
  },

  analytics: {
    title: "Analytics",
    subtitle: "Performance overview and statistics",
    pleaseLogin: "Please log in to access analytics.",
    totalRevenue: "Total Revenue",
    fromOrders: "From {count} orders",
    orders: "Orders",
    paidOrders: "Paid orders",
    conversion: "Conversion",
    codesUsed: "{converted} / {total} codes",
    projects: "Projects",
    imagesCount: "{count} images",
    revenueByMonth: "Revenue by Month",
    last12Months: "Last 12 months",
    topProjects: "Top Projects",
    top5Projects: "Top 5 projects by revenue",
    revenueLabel: "Revenue ({currency})",
  },

  systemLogs: {
    title: "System Logs",
    subtitle: "All system activities and API calls",
    filters: "Filters & Search",
    refresh: "Refresh",
    searchPlaceholder: "Search by user, action, details...",
    allActions: "All actions",
    actions: {
      orderCreate: "Order created",
      orderUpdate: "Order updated",
      projectCreate: "Project created",
      imageUpload: "Image upload",
      userLogin: "User login",
      userLogout: "User logout",
      paymentSuccess: "Payment success",
      paymentFailed: "Payment failed",
    },
    allLevels: "All levels",
    levels: {
      info: "Info",
      warning: "Warning",
      error: "Error",
      success: "Success",
    },
    clear: "Clear",
    found: "{count} entries found",
    pageOf: "Page {page} of {total}",
    columns: {
      timestamp: "Timestamp",
      level: "Level",
      action: "Action",
      user: "User",
      details: "Details",
      ip: "IP",
    },
    noneFound: "No logs found",
  },

  dashboard: {
    welcomeBack: "Welcome back",
    pleaseLogin: "Please log in to access the dashboard.",
    manage: "Manage your photography projects, track sales, and analyze performance.",
    myProjects: "My Projects",
    quickStats: "Quick Stats",
    totalProjects: "Total Projects",
    totalImages: "Total Images",
    totalOrders: "Total Orders",
    totalRevenue: "Total Revenue",
    quickActions: "Quick Actions",
    createProject: "Create New Project",
    viewAnalytics: "View Analytics",
    manageSettings: "Manage Settings",
    recentActivity: "Recent Activity",
    recentProjects: "Recent Projects",
    allProjects: "All Projects",
    noProjectsTitle: "No projects",
    noProjectsDescription: "Get started by creating your first photography project and start selling images.",
    createFirstProject: "Create first project",
    manageSettingsDescription: "Configure settings",
  },

  projects: {
    title: "Projects",
    createProject: "Create Project",
    editProject: "Edit Project",
    deleteProject: "Delete Project",
    projectName: "Project Name",
    projectNumber: "Project Number",
    projectDescription: "Project Description",
    projectStatus: "Project Status",
    draft: "Draft",
    active: "Active",
    archived: "Archived",
    totalImages: "Total Images",
    totalCodes: "Total Codes",
    totalViews: "Total Views",
    createdAt: "Created",
    updatedAt: "Updated",
    selectAvatar: "Select Avatar",
    noProjects: "No projects",
    createFirstProject: "Create your first project",
    confirmDelete: "Are you sure you want to delete this project?",
    projectDeleted: "Project deleted",
    projectCreated: "Project created",
    projectUpdated: "Project updated",
    backToDashboard: "Back to Dashboard",
    projectsCount: "{count} project | {count} projects",
    noDescription: "No description",
    viewImages: "View Images",
    viewVideos: "View Videos",
    viewCodes: "Access Codes",
    editButton: "Edit",
    deleteButton: "Delete",
  },

  orders: {
    title: "Orders",
    orderNumber: "Order Number",
    orderDate: "Order Date",
    orderStatus: "Order Status",
    pending: "Pending",
    paid: "Paid",
    failed: "Failed",
    refunded: "Refunded",
    shipped: "Shipped",
    customerName: "Customer Name",
    customerEmail: "Customer Email",
    totalAmount: "Total Amount",
    totalOrders: "Total Orders",
    items: "Items",
    viewOrder: "View Order",
    noOrders: "No orders",
    orderDetails: "Order Details",
    orderInfo: "Order Information",
    paymentDetails: "Payment Details",
    transactionId: "Transaction ID",
    paymentMethod: "Payment Method",
    paymentStatus: "Status",
    downloadAll: "Download all images",
    download: "Download",
    image: "Image",
    imagesAvailableAfterPayment: "Images will be available for download after successful payment.",
    imageCount: "image",
    imageCountPlural: "images",
    ordersDescription: "Overview of all orders from your projects",
    project: "Project",
    shipping: "Shipping",
    shippingDetails: "Shipping Details",
    shippingAddress: "Shipping Address",
    shippingOverview: "Shipping Overview",
    manageShipping: "Manage shipping for order with print items",
    printItems: "Print Items",
    printItemsTotal: "Print Items Total",
    loadingOrder: "Loading order details...",
    orderNotFound: "Order not found.",
    noOrderSelected: "No order selected.",
    noPrintItems: "This order has no print items requiring shipping.",
    printShippingLabel: "Print Shipping Label",
    markAsShipped: "Mark as Shipped",
    viewFullOrder: "View Full Order",
    backToOrders: "Back to Orders",
    requiresShipping: "Shipping",
    delivery: "Delivery",
    pickup: "Pickup",
    shippingLabel: "SHIPPING LABEL",
    shipTo: "SHIP TO:",
    packageContents: "PACKAGE CONTENTS",
    printLabel: "Print Label",
    deliveryNote: "Delivery Note",
    standardPrint: "Standard Print",
    cancelled: "Cancelled",
    cancelOrder: "Cancel Order",
    orderCancelled: "Order cancelled successfully",
    confirmCancel: "Are you sure you want to cancel this order? This action cannot be undone.",
    filters: "Filters",
    filtered: "filtered",
    searchPlaceholder: "Search by order number, name, or email...",
    allStatuses: "All statuses",
    allProjects: "All projects",
    clearFilters: "Clear filters",
    downloaded: "Downloaded",
    downloadedAt: "Downloaded at",
    notDownloadedYet: "Not downloaded yet",
    markAsPaid: "Mark as Paid",
    markingAsPaid: "Marking as paid...",
    markedAsPaid: "Order successfully marked as paid",
    downloadInvoice: "Download Invoice",
    noInvoice: "No invoice available",
    invoiceDownloaded: "Invoice downloaded",
  },

  cart: {
    title: "Cart",
    emptyCart: "Your cart is empty",
    continueShopping: "Continue Shopping",
    itemRemoved: "Item removed from cart",
    cartUpdated: "Cart updated",
    item: "item",
    items: "items",
    subtotal: "Subtotal",
    total: "Total",
    proceedToCheckout: "Proceed to Checkout",
    checkout: "Proceed to Checkout",
  },

  checkout: {
    title: "Checkout",
    customerInfo: "Customer Information",
    customerInfoDesc: "Enter your details for the order",
    digitalDeliveryOnly: "Digital delivery – no address required.",
    name: "Name",
    namePlaceholder: "Full name",
    email: "Email",
    emailPlaceholder: "your@email.com",
    addressLine1: "Address line 1",
    addressLine1Placeholder: "Street and number",
    addressLine2: "Address line 2",
    addressLine2Placeholder: "Apartment / Suite (optional)",
    postalCode: "Postal code",
    postalCodePlaceholder: "ZIP / Postal code",
    city: "City",
    cityPlaceholder: "City",
    country: "Country",
    countryPlaceholder: "Select country",
    phone: "Phone",
    phonePlaceholder: "Phone number",
    deliveryType: "Delivery Type",
    homeDelivery: "Home Delivery",
    selfPickup: "Self Pickup",
    billingAddress: "Billing Address",
    differentBillingAddress: "Different Billing Address",
    billingAddressLine1: "Billing Address Line 1",
    billingAddressLine2: "Billing Address Line 2",
    billingPostalCode: "Billing Postal Code",
    billingCity: "Billing City",
    billingCountry: "Billing Country",
    shippingCost: "Shipping Cost",
    shippingCostDesc: "Enter the shipping cost for physical products.",
    paymentMethod: "Payment Method",
    orderSummary: "Order Summary",
    payWithPayPal: "Pay securely with PayPal",
    placeOrder: "Place Order",
    paymentSuccess: "Payment successful!",
    paymentFailed: "Payment failed",
    pleaseEnterDetails: "Please enter your details",
    paypalNotConfigured: "PayPal is not configured",
    thankYou: "Thank you for your order!",
    orderConfirmation: "Your order has been received and will be processed shortly.",
    downloadInstructions: "You will receive an email with links to download your images.",
    backToGallery: "Back to Gallery",
    orderSuccess: "Order Successful!",
    orderSuccessDesc: "Your order has been successfully placed.",
    downloadInfoSent: "Download information has been sent to your email.",
    subtotal: "Subtotal",
    processing: "Processing...",
    pickupAddress: "Pickup",
  },

  gallery: {
    alreadyPurchased: "Already purchased",
    title: "Gallery",
    enterAccessCode: "Enter Access Code",
    accessCode: "Access Code",
    projectNumber: "Project Number",
    login: "Login",
    projectNotActiveTitle: "Gallery not yet available",
    projectNotActiveBody: "Leave your email and we’ll notify you automatically once content is published.",
    yourEmail: "Your email",
    emailPlaceholder: "you@example.com",
    notifyMe: "Notify me",
    notifySuccess: "Got it! We’ll email you when it’s live.",
    invalidCode: "Invalid access code",
    accessCodeLimitReached: "Access code usage limit reached",
    selectImages: "Select Images",
    addToCart: "Add to Cart",
    viewCart: "View Cart",
    noImagesSelected: "No images selected",
    scanQRCode: "Or scan QR code",
    scanInstruction: "Scan the QR code from your card",
    emptyGallery: "No images or videos yet",
    selectProductType: "Select product type",
    productType: "Product type",
    digital: "Digital (Download)",
    print: "Print (Physical photo)",
    selectFormat: "Select format",
    loadingFormats: "Loading formats...",
    noFormatsAvailable: "No print formats available. Please contact the photographer.",
    addToCartButton: "Add to cart",
    logout: "Logout",
    sessionExpired: "Your session has expired. Please log in again.",
  },

  shop: {
    imprint: "Imprint",
    privacyPolicy: "Privacy Policy",
    terms: "Terms & Conditions",
    contact: "Contact",
    returns: "Returns",
    shipping: "Shipping",
    payment: "Payment Methods",
    footerDisclaimer: "All prices incl. applicable VAT. Subject to change.",
  },

  validation: {
    required: "This field is required",
    invalidEmail: "Invalid email address",
    passwordTooShort: "Password must be at least 8 characters",
    passwordMismatch: "Passwords do not match",
  },

  stats: {
    totalProjects: "Total Projects",
    totalImages: "Total Images",
    totalOrders: "Total Orders",
    totalRevenue: "Total Revenue",
  },

  downloads: {
    title: "Downloads",
    errorTitle: "Download Error",
    invalidOrExpiredLink: "Invalid or expired download link",
    enterOrderId: "Enter Order ID",
    orderIdLabel: "Order ID",
    orderIdPlaceholder: "Enter order ID",
    submit: "Submit",
    invalidOrderNumber: "Invalid order number",
    allDownloaded: "All images downloaded!",
    downloadAll: "Download All",
    download: "Download",
    orderNotFound: "Order not found or access denied",
    noImages: "No images available for this order",
    downloadStarted: "Download started for",
    downloadError: "Error downloading",
    downloadFailed: "Failed to download {filename}",
    allDownloadsStarted: "All downloads started",
  },

  videos: {
    title: "Videos",
    videoDeleted: "Video deleted",
    downloadLinkError: "Could not create download link",
    backToProject: "Back to Project",
    uploadVideos: "Upload Videos",
    selectFiles: "Select Files",
    maxFileSize: "Maximum file size: 15GB per video",
    dragDrop: "Drag and drop video files here or click to select",
    uploading: "Uploading...",
    completed: "Completed",
    pending: "Pending",
    error: "Error",
    confirmDelete: "Are you sure you want to delete this video?",
    deleteVideo: "Delete Video",
    noVideos: "No videos",
    uploadFirstVideo: "Upload your first video",
    uploadFailed: "Upload failed",
  },

  forgotPassword: {
    title: "Forgot Password",
    subtitle: "Reset your password",
    emailSent: "Email sent",
    checkInbox: "Check your email inbox",
    emailSentMessage: "If there is an account with the email address <strong>{email}</strong>, we have sent you a password reset link.",
    checkSpam: "Please also check your spam folder if you don't see the email within a few minutes.",
    backToLogin: "Back to Login",
    enterEmail: "Enter your email address",
    emailPlaceholder: "your@email.com",
    sendResetLink: "Send reset link",
    resetInstructions: "Enter your email address and we'll send you a link to reset your password.",
  },

  resetPassword: {
    title: "Reset Password",
    subtitle: "Enter your new password",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    resetButton: "Reset Password",
    success: "Password changed successfully!",
    successMessage: "Your password has been changed. You will be redirected to the login page.",
    invalidToken: "Invalid or expired token",
    invalidTokenMessage: "This reset link is invalid or has expired. Please request a new password reset.",
    requestNew: "Request new reset",
    verifying: "Verifying reset link...",
    passwordMismatch: "Passwords do not match",
    passwordTooShort: "Password must be at least 8 characters",
  },

  notFound: {
    title: "404",
    subtitle: "Page Not Found",
    message: "Sorry, the page you are looking for doesn't exist.",
    submessage: "It may have been moved or deleted.",
    goHome: "Go Home",
  },

  register: {
    title: "Create Account",
    subtitle: "Create your {appTitle} account",
    name: "Full Name",
    namePlaceholder: "John Doe",
    email: "Email",
    emailPlaceholder: "your@email.com",
    password: "Password",
    passwordPlaceholder: "At least 8 characters",
    confirmPassword: "Confirm Password",
    confirmPasswordPlaceholder: "Repeat your password",
    registerButton: "Create Account",
    alreadyHaveAccount: "Already have an account?",
    loginLink: "Login here",
    accountCreated: "Account created successfully!",
    accountCreatedPendingActivation: "Registration successful! Your account needs to be activated by an administrator before you can log in.",
    registrationFailed: "Registration failed",
    passwordsDontMatch: "Passwords do not match",
    passwordTooShort: "Password must be at least 8 characters",
  },

  login: {
    title: "Welcome back",
    subtitle: "Sign in to your {appTitle} account",
    email: "Email",
    emailPlaceholder: "your@email.com",
    password: "Password",
    passwordPlaceholder: "Your password",
    rememberDevice: "Remember this device",
    loginButton: "Sign In",
    forgotPassword: "Forgot your password?",
    notRegistered: "Not registered yet?",
    registerLink: "Create an account",
    loginSuccess: "Login successful!",
    loginFailed: "Login failed",
    invalidCredentials: "Invalid email or password",
  },

  pdf: {
    project: "Project",
    scanOrEnter: "Scan QR code or enter code",
  },

  profile: {
    title: "Profile",
    subtitle: "Manage your profile and security settings",
    editProfile: "Edit Profile",
    changePassword: "Change Password",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    profileUpdated: "Profile successfully updated!",
    passwordChanged: "Password successfully changed!",
    profileInfo: "Profile Information",
    profileInfoDesc: "Update your name and email address",
    name: "Name",
    email: "Email",
    security: "Security",
    securityDesc: "Change your password",
    oldPassword: "Current Password",
    newPasswordLabel: "New Password",
    confirmPasswordLabel: "Confirm New Password",
    updateProfile: "Update Profile",
    updatePassword: "Change Password",
    accountInfo: "Account Information",
    accountDetails: "Details about your account",
    role: "Role",
    roleAdmin: "Administrator",
    roleUser: "User",
    createdAt: "Created",
    lastLogin: "Last login",
    logoutSection: "Logout",
    logoutDesc: "Log out of your account",
    logoutButton: "Log out",
    errors: {
      profileUpdateFailed: "Failed to update profile",
      passwordChangeFailed: "Failed to change password",
      nameEmailRequired: "Name and email are required",
      allFieldsRequired: "All fields are required",
      passwordMismatch: "New passwords do not match",
      passwordTooShort: "New password must be at least 8 characters",
    },
  },

  images: {
    title: "Images",
    uploadImages: "Upload Images",
    uploadInProgress: "Upload in progress...",
    dragAndDrop: "Drag images here or click to select",
    selectImages: "Select Images",
    imageCount: "Image Count",
    totalSize: "Total Size",
    setAsAvatar: "Set as Avatar",
    avatarSet: "Avatar set",
    deleteImage: "Delete Image",
    confirmDeleteImage: "Are you sure you want to delete this image?",
    imageDeleted: "Image deleted",
    imagesUploaded: "Images uploaded",
    uploadFailed: "Upload failed",
    noImages: "No images",
    uploadFirstImage: "Upload your first image",
    preview: "Preview",
    original: "Original",
    thumbnail: "Thumbnail",
    dimensions: "Dimensions",
    fileSize: "File Size",
    uploadedAt: "Uploaded",
      gcsNotConfigured: "GCS is not configured",
      imageNotAvailable: "Image not available",
      noExifData: "No EXIF metadata available for this image.",
    downloadError: "Error downloading image",
    downloadErrorMultiple: "Error downloading images",
    downloadSuccess: "Downloading",
    downloadAllSuccess: "Downloading images...",
  },

  theme: {
    toggleDark: "Switch to dark theme",
    toggleLight: "Switch to light theme",
  },

  packages: {
    title: "Packages",
    description: "Manage predefined packages for your customers",
    createPackage: "Create Package",
    editPackage: "Edit Package",
    packageName: "Package Name",
    packageDescription: "Description",
    packagePrice: "Price",
    isActive: "Active",
    items: "Contents",
    addItem: "Add Item",
    itemType: "Type",
    itemQuantity: "Quantity",
    printFormat: "Print Format",
    digital: "Digital Images",
    print: "Physical Print",
    video: "Videos",
    noPackages: "No packages available",
    createFirstPackage: "Create your first package",
    packageCreated: "Package created",
    packageUpdated: "Package updated",
    packageDeleted: "Package deleted",
    deletePackage: "Delete Package",
    deletePackageConfirm: "Are you sure you want to delete this package?",
    printFormatRequired: "Please select a print format for every print item.",
    packageSelection: "Select Package",
    selectPackage: "Choose a package",
    manualSelection: "Manual Selection",
    manualSelectionDescription: "Select individual media without a package",
    packageIncludes: "Package includes:",
    digitalImages: "{count} digital images",
    physicalPrints: "{count}x Print {format}",
    videos: "{count} videos",
    selectThisPackage: "Select this package",
    selectedPackage: "Selected Package",
    packageLimit: "Package limit reached",
    packageLimitWarning: "You have reached the limit for this media type",
    remainingDigital: "Remaining: {count} images",
    remainingPrints: "Remaining: {count} prints",
    remainingVideos: "Remaining: {count} videos",
  },

  accessCodes: {
    title: "Access Codes",
    generateCodes: "Generate Codes",
    codesGenerated: "Codes generated",
    numberOfCodes: "Number of codes",
    generateCodesDescription: "Enter the number of codes to generate",
    downloadPDF: "Download PDF",
    pdfGenerated: "PDF generated",
    code: "Code",
    usageCount: "Usage count",
    lastUsed: "Last used",
    invalidated: "Invalidated",
    invalidate: "Invalidate",
    confirmInvalidate: "Are you sure you want to invalidate this code?",
    codeInvalidated: "Code invalidated",
    noCodes: "No access codes",
    generateFirstCodes: "Generate first codes",
    printInstructions: "Print or share access codes with your clients",
    active: "Active",
    used: "Used",
    downloadImage: "Download as image",
    imageDownloaded: "QR code downloaded as image",
  },
};

export type Translation = typeof en;